import type { DetailedHTMLProps, InputHTMLAttributes, FocusEvent, PropsWithoutRef, ReactNode } from 'react';
import React, { forwardRef, useState, useRef } from 'react';

import { Icon, Label } from '../../atoms';

import {
  ComponentContainer,
  Hint,
  InputContainer,
  AutocompleteInputContainer,
  StyledInput,
  ErrorText,
  ErrorIconContainer,
  StyledInputContainer,
} from './text-field-styles';

export type TextFieldProps = PropsWithoutRef<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
> & {
  id: string;
  label?: string;
  hint?: string;
  error?: { message?: string; type?: string } | boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  autocompleteItems?: ReactNode;
  showErrorIcon?: boolean;
  className?: string;
  as?: 'input' | 'textarea';
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    id,
    label,
    hint,
    error,
    disabled,
    startAdornment,
    endAdornment,
    autocompleteItems,
    className,
    showErrorIcon = true,
    onBlur = () => void 0,
    onFocus = () => void 0,
    as = 'input',
    ...inputProps
  } = props;
  const errorId = `_input_${props.id ?? props.name}_error_message`;
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleBlur = (evt: FocusEvent<HTMLInputElement>) => {
    if (containerRef && !containerRef.current?.contains(evt.relatedTarget)) {
      setFocused(false);
      onBlur(evt);
    }
  };
  const handleFocus = (evt: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus(evt);
  };
  return (
    <ComponentContainer ref={containerRef} className={className} onBlur={handleBlur}>
      {label && <Label htmlFor={id}>{label}</Label>}
      {hint && <Hint>{hint}</Hint>}
      <InputContainer
        disabled={disabled}
        error={!!error}
        focused={focused}
        hasAutocomplete={Boolean(autocompleteItems)}
      >
        <StyledInputContainer hasAutocomplete={Boolean(autocompleteItems)} disabled={disabled}>
          {startAdornment}
          <StyledInput
            as={as}
            id={id}
            aria-label={label}
            aria-invalid={!!error}
            aria-describedby={errorId}
            disabled={disabled}
            onFocus={handleFocus}
            ref={ref}
            {...inputProps}
          />
          {error && showErrorIcon && (
            <ErrorIconContainer>
              <Icon name="Error" color="red" />
            </ErrorIconContainer>
          )}
          {endAdornment}
        </StyledInputContainer>
        {focused && autocompleteItems && <AutocompleteInputContainer>{autocompleteItems}</AutocompleteInputContainer>}
      </InputContainer>
      {typeof error === 'object' && 'message' in error && (
        <ErrorText role="alert" id={errorId}>
          {error.message}
        </ErrorText>
      )}
    </ComponentContainer>
  );
});

export const StyledTextField = (props: any) => <TextField {...props} />;

TextField.displayName = 'TextField';
